import { Injectable } from "@angular/core";
import { ApiService, Encounter, ErrorMessage, HarloweSupport, SignalingService } from "@harlowe/core";
import { MessageService } from "primeng/api";
import { Diagnosis, Modifier, Procedure } from "@harlowe/core";
import { EMPTY, catchError, forkJoin, lastValueFrom, map, merge, mergeMap, of, timeout } from "rxjs";
import { environment } from "../../environments/environment";
import { FacilityLocation, MedicalService, DraftMedicalBill, MedicalBillLine, MedicalBillLineInsert, MedicalBillLineUpdate, MedicalBillUpdate, ProviderFilter } from "@harlowe/core";
import { HubConnection } from "@microsoft/signalr";
import { Encounters } from "../state/encounters/encounter-actions";
import { Store } from "@ngxs/store";
import { TealCompEncounterHeader } from "../models/encounter";
import { MedicalBill } from "../models/medical-bill";

export abstract class MedicalServiceBase {
    abstract searchProcedures(q: string): Promise<Procedure[]>;
    abstract searchFacilities(q: string): Promise<FacilityLocation[]>;
    abstract searchModifiers(q: string): Promise<Modifier[]>;
    abstract searchDiagnoses(q: string): Promise<Diagnosis[]>;


    abstract setMedicalBillImage(billId: number, file: File): Promise<HarloweSupport.File>
    abstract clearMedicalBillImage(billId: number): Promise<HarloweSupport.File>
    abstract setMedicalBillReport(billId: number, file: File): Promise<HarloweSupport.File>
    abstract clearMedicalBillReport(billId: number): Promise<HarloweSupport.File>
    abstract updateMedicalBill(options: { billId: number, medicalBill: MedicalBillUpdate, insertLines?: MedicalBillLineInsert[], updateLines?: MedicalBillLineUpdate[], deleteLines?: number[], encounterId?: number }): Promise<MedicalBill>;
    abstract recalculateAllowedAmount(billId: number, override: boolean, discountAmount?: number): Promise<number>;
    abstract recalculateMarkedUpAmount(billId: number, override: boolean, markupAmount?: number): Promise<number>;
    abstract getEncountersForMedicalBill(billId: number): Promise<number[]>;
    abstract createMedicalBillFromImage(billImageFile: File): Promise<MedicalBill>;

}



@Injectable({ providedIn: 'root' })
export class TealCompMedicalService extends MedicalService {
    override getEligibleEncountersForPatient<E extends Encounter>(patientId: number): Promise<Encounter[]> {
        throw new Error("Method not implemented.");
    }
    override getEligibleEncountersForDraftBill(draft: DraftMedicalBill): Promise<Encounter[]> {
        throw new Error("Method not implemented.");
    }

    private _medicalBillHub: HubConnection;

    constructor
        (
    ) {
        super();
        this._medicalBillHub = this._signaling.generateHub(environment.harlowe.tealcomp.baseUri + environment.harlowe.tealcomp.signaling.endpoints.medicalBills);

        this._medicalBillHub.start();
    }

    override searchProcedures(q: string): Promise<Procedure[]> {
        return lastValueFrom
            (
                this._api.makeAPIGetCall<Procedure[]>(environment.harlowe.tealcomp.baseUri, "/procedures", { query: q })
                    .pipe(map(procedures => procedures.map(p => new Procedure(p.id, p.code, p.description, p.codeSet, p.category))))
            );
    }

    override searchFacilities(q: string): Promise<FacilityLocation[]> {
        return lastValueFrom
            (
                this._api.makeAPIGetCall<FacilityLocation[]>(environment.harlowe.tealcomp.baseUri, "/facilities", { query: q })
                    .pipe(map(procedures => procedures.map(p => new FacilityLocation(p.facility, p.location))))
            );
    }

    override searchProviders(filter: ProviderFilter): Promise<FacilityLocation[]> {
        return lastValueFrom
            (
                this._api.makeAPIGetCall<FacilityLocation[]>(environment.harlowe.tealcomp.baseUri, "/providers",
                {
                    ...filter.name     && { name:     filter.name },
                    ...filter.ein      && { ein:      filter.ein },
                    ...filter.location && { location: filter.location }
                })
                .pipe(map(results => results.map(r => new FacilityLocation(r.facility, r.location))))
            );
    }

    override searchModifiers(q: string): Promise<Modifier[]> {
        return lastValueFrom
            (
                this._api.makeAPIGetCall<Modifier[]>(environment.harlowe.tealcomp.baseUri, "/modifiers", { query: q })
                    .pipe(map(modifiers => modifiers.map(m => new Modifier(m.code, m.description, m.modifierMeaning))))
            )
    }

    override searchDiagnoses(q: string): Promise<Diagnosis[]> {
        return lastValueFrom
            (
                this._api.makeAPIGetCall<Diagnosis[]>(environment.harlowe.tealcomp.baseUri, "/diagnoses", { query: q })
                    .pipe(map(diagnoses => diagnoses.map(d => new Diagnosis(d.code, d.description))))
            )
    }


    setMedicalBillImage(billId: number, file: File): Promise<HarloweSupport.File> {
        var d: FormData = new FormData();
        d.append('billImageFile', file);

        return lastValueFrom
            (
                this._api.makeAPIPutCall<HarloweSupport.File>(environment.harlowe.tealcomp.baseUri, "/medicalbill/" + billId.toString() + "/image", d)
                    .pipe
                    (
                        map
                            (
                                img => {
                                    this.getEncountersForMedicalBill(billId)
                                        .then
                                        (
                                            encounters => encounters.forEach(e => this._store.dispatch(new Encounters.MedicalBillImageAdded({ encounterId: e, billId: billId })))
                                        )
                                    this._messages.add({ key: 'main-alert', severity: 'success', summary: 'Success', detail: 'Bill Image File Uploaded' })
                                    return img;
                                }
                            )
                    )
            )
    }

    createMedicalBillFromImage(billImageFile: File): Promise<MedicalBill> {
        var d: FormData = new FormData();
        d.append('billImageFile', billImageFile);

        return lastValueFrom
            (
                this._api.makeAPIPostCall<MedicalBill>(environment.harlowe.tealcomp.baseUri, "/medicalbill/createFromImage", d)
                    .pipe
                    (
                        timeout(60000), //Allow a 60 second timeout for this one... it's a lot of work. 30 seconds may not be enough
                        map
                            (
                                bill => {
                                    this.getEncountersForMedicalBill(bill.id)
                                        .then
                                        (
                                            encounters => encounters.forEach(e => this._store.dispatch(new Encounters.MedicalBillAdded({ encounterId: e, medicalBill: bill })))
                                        )
                                    this._messages.add({ key: 'main-alert', severity: 'success', summary: 'Success', detail: 'Medical bill added from image' })
                                    // this._store.dispatch(new MedicalBills.MedicalBillImageSet({billId:billId,billImage:img}));
                                    return bill;
                                }
                            ),
                        catchError((error: ErrorMessage) => { this._messages.add({ key: 'main-alert', severity: 'error', summary: error.message }); return of() })
                    )
            )

    }


    setMedicalBillReport(billId: number, file: File): Promise<HarloweSupport.File> {
        var d: FormData = new FormData();
        d.append('billReportFile', file);

        return lastValueFrom
            (
                this._api.makeAPIPutCall<HarloweSupport.File>(environment.harlowe.tealcomp.baseUri, "/medicalbill/" + billId.toString() + "/report", d)
                    .pipe
                    (
                        map
                            (
                                img => {
                                    this.getEncountersForMedicalBill(billId)
                                        .then
                                        (
                                            encounters => encounters.forEach(e => this._store.dispatch(new Encounters.MedicalBillImageAdded({ encounterId: e, billId: billId })))
                                        )
                                    this._messages.add({ key: 'main-alert', severity: 'success', summary: 'Success', detail: 'Bill Report File Uploaded' })
                                    return img;
                                }
                            )
                    )
            )
    }

    getEncountersForMedicalBill(billId: number): Promise<number[]> {
        return this._medicalBillHub.invoke<TealCompEncounterHeader[]>("GetBillEncounters", billId)
            .then
            (
                encounters => encounters.map(e => e.id)
            )
    }

    clearMedicalBillImage(billId: number): Promise<HarloweSupport.File> {
        return lastValueFrom
            (
                this._api.makeAPIDeleteCall<HarloweSupport.File>(environment.harlowe.tealcomp.baseUri, "/medicalbill/" + billId.toString() + "/image")
                    .pipe
                    (
                        map
                            (
                                img => {
                                    this.getEncountersForMedicalBill(billId)
                                        .then
                                        (
                                            encounters => encounters.forEach(e => this._store.dispatch(new Encounters.MedicalBillImageRemoved({ encounterId: e, billId: billId })))
                                        )
                                    this._messages.add({ key: 'main-alert', severity: 'success', summary: 'Success', detail: 'Bill Image File Cleared' })
                                    return img;
                                }
                            )
                    )
            )
    }

    clearMedicalBillReport(billId: number): Promise<HarloweSupport.File> {
        return lastValueFrom
            (
                this._api.makeAPIDeleteCall<HarloweSupport.File>(environment.harlowe.tealcomp.baseUri, "/medicalbill/" + billId.toString() + "/report")
                    .pipe
                    (
                        map
                            (
                                img => {
                                    this.getEncountersForMedicalBill(billId)
                                        .then
                                        (
                                            encounters => encounters.forEach(e => this._store.dispatch(new Encounters.MedicalBillImageRemoved({ encounterId: e, billId: billId })))
                                        )
                                    this._messages.add({ key: 'main-alert', severity: 'success', summary: 'Success', detail: 'Bill Report File Cleared' })
                                    return img;
                                }
                            )
                    )
            )
    }

    recalculateAllowedAmount(billId: number, override: boolean, discountAmount?: number): Promise<number> {
        return this._medicalBillHub.invoke<number>("RecalculateAllowedAmount", billId, override, discountAmount);
    }

    recalculateMarkedUpAmount(billId: number, override: boolean, markupAmount?: number): Promise<number> {
        return this._medicalBillHub.invoke<number>("RecalculateMarkupAmount", billId, override, markupAmount);
    }

    updateMedicalBill(options: { billId: number, medicalBill: MedicalBillUpdate, insertLines?: MedicalBillLineInsert[], updateLines?: MedicalBillLineUpdate[], deleteLines?: number[], encounterId?: number }): Promise<MedicalBill> {
        return lastValueFrom
            (

                //Update lines first
                forkJoin
                    (
                        [
                            of({}),
                            ...options.updateLines ? options.updateLines.map(l => this._api.makeAPIPatchCall<MedicalBillLine>(this._baseUrl, "/medicalbill/" + options.medicalBill.id.toString() + "/lines/" + l.id.toString(), l)) : [],
                            ...options.insertLines ? options.insertLines.map(l => this._api.makeAPIPutCall<MedicalBillLine>(this._baseUrl, "/medicalbill/" + options.medicalBill.id.toString() + "/lines/", l)) : [],
                            ...options.deleteLines ? options.deleteLines.map(l => this._api.makeAPIDeleteCall<MedicalBillLine>(this._baseUrl, "/medicalbill/" + options.medicalBill.id.toString() + "/lines/" + l.toString())) : []
                        ]
                    )

                    //Then update the bill and return it
                    .pipe
                    (
                        mergeMap
                            (
                                lines => this._api.makeAPIPatchCall<MedicalBill>(this._baseUrl, "/medicalbill/" + options.medicalBill.id.toString(), options.medicalBill)
                            )
                        , map
                            (
                                bill => {
                                    this._messages.add({ key: 'main-alert', severity: 'success', summary: 'Success', detail: 'Updated Medical Bill' })

                                    if (options.encounterId)
                                        this._store.dispatch(new Encounters.MedicalBillUpdated({ encounterId: options.encounterId, medicalBill: bill }));
                                    return bill;
                                }

                            )
                    )
            );
    }
}